import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import {
  AgentFormResponseMode,
  TSysAgentStepFormResponseSource,
  TWorkflowDefinition,
} from '@commenda/types';
import { useMemo } from 'react';
import { WorkflowGraphHelper, WorkflowStepPickerHelper } from '../../helpers';

interface IAgentFormResponsesFieldProps {
  workflow: TWorkflowDefinition | null;
  agentStepId: string;
  source: TSysAgentStepFormResponseSource | undefined;
  onChange: (source: TSysAgentStepFormResponseSource) => void;
}

export const AgentFormResponsesField = ({
  workflow,
  agentStepId,
  source,
  onChange,
}: IAgentFormResponsesFieldProps) => {
  const mode = source?.mode ?? AgentFormResponseMode.NONE;

  // Memoised because MultiSelect re-seeds its internal state whenever defaultValue changes identity; a fresh
  // array literal every render would reset the ticks on each keystroke elsewhere in the panel.
  const selectedStepIds = useMemo(
    () =>
      source?.mode === AgentFormResponseMode.SELECTED
        ? (source.stepIds ?? [])
        : [],
    [source]
  );

  const upstreamFormSteps = useMemo(
    () => WorkflowGraphHelper.findUpstreamFormSteps(workflow, agentStepId),
    [workflow, agentStepId]
  );

  const options = useMemo(
    () =>
      WorkflowStepPickerHelper.buildStepPickerOptions(
        workflow,
        upstreamFormSteps,
        selectedStepIds
      ),
    [upstreamFormSteps, selectedStepIds, workflow]
  );

  const strandedCount = options.filter((option) => option.isStranded).length;

  return (
    <FieldWrapper>
      <Label>Feed in form responses</Label>
      <Select
        value={mode}
        onValueChange={(value) => {
          const nextMode = value as AgentFormResponseMode;
          onChange(
            nextMode === AgentFormResponseMode.SELECTED
              ? { mode: nextMode, stepIds: selectedStepIds }
              : { mode: nextMode }
          );
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select which form responses to feed in" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AgentFormResponseMode.NONE}>None</SelectItem>
          <SelectItem value={AgentFormResponseMode.ALL_PREVIOUS}>
            All previous form steps
          </SelectItem>
          <SelectItem value={AgentFormResponseMode.SELECTED}>
            Selected steps
          </SelectItem>
        </SelectContent>
      </Select>

      {mode === AgentFormResponseMode.SELECTED && (
        <>
          <MultiSelect
            key={agentStepId}
            options={options}
            defaultValue={selectedStepIds}
            onValueChange={(stepIds) =>
              onChange({ mode: AgentFormResponseMode.SELECTED, stepIds })
            }
            placeholder="Select form steps"
            notFoundText="No form step runs before this one. Connect one first."
          />
          {strandedCount > 0 && (
            <InputErrorMessage
              message={
                strandedCount === 1
                  ? 'One selected form no longer runs before this step. Untick it, or reconnect it earlier — publishing will refuse it.'
                  : `${strandedCount} selected forms no longer run before this step. Untick them, or reconnect them earlier — publishing will refuse them.`
              }
            />
          )}
        </>
      )}

      <p className="txt-extra-small-regular mt-xs text-text-secondary">
        Whole forms only. The agent is told when a selected form has no
        submission, rather than left to guess.
      </p>
    </FieldWrapper>
  );
};
